import { useCallback } from "react";
import { useNavigate } from "react-router";
import { apiClient, call } from "../../shared/api/client";
import { isApiProblem } from "../../shared/api/problem";
import { normalizeGlobalId } from "../../shared/lib/globalId";

const KEY_SHAPE = /^[A-Za-z][A-Za-z0-9]{1,9}-[1-9][0-9]*$/;

/** True when text is typed like a global ID (GR…), after NFKC. */
export function looksLikeGlobalId(text: string): boolean {
  return /^g\s*r/i.test(text.normalize("NFKC").trim());
}

export function looksLikeKey(text: string): boolean {
  return KEY_SHAPE.test(text.normalize("NFKC").trim());
}

/** GRF-28: a bare item number as typed ("884"), after NFKC. */
export function looksLikeItemNumber(text: string): boolean {
  return /^[0-9]{1,9}$/.test(text.normalize("NFKC").trim()) && Number(text.normalize("NFKC").trim()) > 0;
}

/**
 * Header search (UX-01 4.1): a key, an alias or a global ID opens the item through /resolve; anything else,
 * or a ref that resolves to nothing, goes to the search page. A global ID with a wrong check character goes to
 * the search page, which says so instead of "No results".
 * GRF-28: a bare number ("884") opens `<project>-884` in the project the person is on (`projectKey`), or the one item
 * with that number when there is no project context; none or several go to the search page, which lists every match.
 */
export function useJumpToRef() {
  const navigate = useNavigate();
  return useCallback(
    async (text: string, projectKey?: string) => {
      const query = text.trim();
      if (!query) return;
      const gid = normalizeGlobalId(query);
      const number = looksLikeItemNumber(query);
      const resolvable = (looksLikeGlobalId(query) && gid.valid) || looksLikeKey(query) || number;
      if (resolvable) {
        try {
          const ref = number ? query.normalize("NFKC") : gid.valid ? gid.globalId : query.normalize("NFKC").toUpperCase();
          const project = number && projectKey ? projectKey : undefined;
          const result = await call(apiClient.GET("/resolve", { params: { query: { ref, project } } }));
          await navigate(`/items/${encodeURIComponent(result.currentKey)}`);
          return;
        } catch (error) {
          if (!isApiProblem(error) || (error.status !== 404 && error.status !== 422)) throw error;
        }
      }
      await navigate(`/search?q=${encodeURIComponent(query)}`);
    },
    [navigate],
  );
}
